import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  RefreshCw, Clock, CheckCircle2, XCircle, DollarSign,
  Banknote, AlertTriangle, Send, Filter, ArrowDownToLine,
} from 'lucide-react';
import { shareConversionApi } from '../../services/api';

/* ───────────────────────── helpers ───────────────────────── */

const formatUsd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

function timeRemaining(deadlineIso: string): { label: string; overdue: boolean } {
  const ms = new Date(deadlineIso).getTime() - Date.now();
  if (ms <= 0) return { label: 'Overdue', overdue: true };
  const hrs = Math.floor(ms / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  return { label: `${hrs}h ${mins}m remaining`, overdue: false };
}

const STATUS_CONFIG: Record<string, { label: string; classes: string; icon: React.ElementType }> = {
  pending: { label: 'Pending', classes: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  approved: { label: 'Approved', classes: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: CheckCircle2 },
  ach_initiated: { label: 'ACH Initiated', classes: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30', icon: Send },
  settled: { label: 'Settled', classes: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle2 },
  rejected: { label: 'Rejected', classes: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
  cancelled: { label: 'Cancelled', classes: 'bg-muted text-muted-foreground border-border', icon: XCircle },
};

/* ───────────────────────── component ───────────────────────── */

export default function LiquidityDistributionsAdminTab() {
  const [requests, setRequests] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rejectNotes, setRejectNotes] = useState<Record<string, string>>({});
  const [showRejectInput, setShowRejectInput] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [reqRes, statsRes] = await Promise.all([
        shareConversionApi.adminListLiquidations({ status: statusFilter || undefined }),
        shareConversionApi.adminGetLiquidationStats(),
      ]);
      if (reqRes.success && reqRes.data) setRequests(reqRes.data);
      if (statsRes.success && statsRes.data) setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to load liquidation data:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const refresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleApprove = async (id: string) => {
    setActionLoading(id);
    try {
      const res = await shareConversionApi.adminApproveLiquidation(id);
      if (res.success) {
        toast.success('Liquidation approved');
        loadData();
      } else toast.error((res as any).error || 'Failed');
    } catch (err: any) { toast.error(err.message || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleReject = async (id: string) => {
    setActionLoading(id);
    try {
      const res = await shareConversionApi.adminRejectLiquidation(id, rejectNotes[id] || '');
      if (res.success) {
        toast.success('Liquidation rejected');
        setShowRejectInput(null);
        loadData();
      } else toast.error((res as any).error || 'Failed');
    } catch (err: any) { toast.error(err.message || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleInitiateAch = async (id: string) => {
    setActionLoading(id);
    try {
      const res = await shareConversionApi.adminInitiateAch(id);
      if (res.success) {
        toast.success('ACH transfer initiated');
        loadData();
      } else toast.error((res as any).error || 'Failed');
    } catch (err: any) { toast.error(err.message || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleSettle = async (id: string) => {
    setActionLoading(id);
    try {
      const res = await shareConversionApi.adminSettleLiquidation(id);
      if (res.success) {
        toast.success('Liquidation settled');
        loadData();
      } else toast.error((res as any).error || 'Failed');
    } catch (err: any) { toast.error(err.message || 'Failed'); }
    finally { setActionLoading(null); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const pendingCount = stats?.pending?.count ?? 0;
  const pendingUsd = stats?.pending?.totalUsd ?? 0;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Pending"
          count={pendingCount}
          usd={pendingUsd}
          icon={Clock}
          color="text-yellow-400"
          accent="border-yellow-500/30"
        />
        <StatCard
          label="Approved"
          count={stats?.approved?.count ?? 0}
          usd={stats?.approved?.totalUsd ?? 0}
          icon={CheckCircle2}
          color="text-blue-400"
          accent="border-blue-500/30"
        />
        <StatCard
          label="ACH In-Flight"
          count={stats?.ach_initiated?.count ?? 0}
          usd={stats?.ach_initiated?.totalUsd ?? 0}
          icon={Send}
          color="text-indigo-400"
          accent="border-indigo-500/30"
        />
        <StatCard
          label="Settled"
          count={stats?.settled?.count ?? 0}
          usd={stats?.settled?.totalUsd ?? 0}
          icon={CheckCircle2}
          color="text-emerald-400"
          accent="border-emerald-500/30"
        />
      </div>

      {/* Urgent Banner */}
      {pendingCount > 0 && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div>
            <p className="text-red-400 font-semibold text-sm">
              {pendingCount} pending liquidation request{pendingCount > 1 ? 's' : ''} requiring action
            </p>
            <p className="text-red-400/70 text-xs mt-0.5">
              Total: {formatUsd(pendingUsd)} — 72-hour SLA applies from request submission
            </p>
          </div>
        </div>
      )}

      {/* Filter + Refresh */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-background border border-input rounded-lg py-1.5 px-3 text-foreground text-sm focus:outline-none focus:border-primary"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="ach_initiated">ACH Initiated</option>
            <option value="settled">Settled</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-1.5 bg-muted/40 hover:bg-muted/70 border border-border rounded-lg text-sm text-foreground transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Requests List */}
      {requests.length === 0 ? (
        <div className="text-center py-16 bg-muted/30 border border-border rounded-xl">
          <Banknote className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-foreground font-medium">No liquidation requests</p>
          <p className="text-muted-foreground text-sm mt-1">
            {statusFilter ? `No requests with status "${statusFilter}"` : 'Requests will appear here when investors submit them'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req: any) => {
            const sc = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
            const StatusIcon = sc.icon;
            const deadline = req.deadlineAt ? timeRemaining(req.deadlineAt) : null;
            const isActioning = actionLoading === req.id;

            return (
              <div key={req.id} className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="p-4 sm:p-5 space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-foreground font-semibold">{req.requestId}</p>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${sc.classes}`}>
                          <StatusIcon className="w-3 h-3" />
                          {sc.label}
                        </span>
                      </div>
                      <p className="text-muted-foreground text-xs mt-1">
                        {req.userName || 'Unknown'} ({req.userEmail || req.contactEmail || 'N/A'})
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-foreground">{formatUsd(req.totalPayoutUsd)}</p>
                      <p className="text-xs text-muted-foreground">total payout</p>
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">Source</p>
                      <p className="text-foreground font-medium">
                        {req.sourceType === 'crypto_position' ? `Crypto (${req.asset})` : 'Investment'}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Share Value</p>
                      <p className="text-foreground font-medium">{formatUsd(req.shareValueUsd)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Yield</p>
                      <p className="text-emerald-400 font-medium">+{formatUsd(req.yieldEarnedUsd)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Fee</p>
                      <p className="text-foreground font-medium">-{formatUsd(req.feeUsd)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Created</p>
                      <p className="text-foreground font-medium">{formatDate(req.createdAt)}</p>
                    </div>
                  </div>

                  {/* Deadline */}
                  {deadline && req.status === 'pending' && (
                    <div className={`flex items-center gap-2 text-xs font-medium ${
                      deadline.overdue ? 'text-red-400' : 'text-yellow-400'
                    }`}>
                      <Clock className="w-3.5 h-3.5" />
                      {deadline.overdue ? '⚠️ OVERDUE — Process immediately' : `⏰ ${deadline.label}`}
                    </div>
                  )}

                  {/* Source ID */}
                  <p className="text-xs text-muted-foreground font-mono">
                    Source: {req.sourceId} • User: {req.userId}
                  </p>

                  {/* ACH Info */}
                  {req.achTransferId && (
                    <p className="text-xs text-muted-foreground">
                      ACH Transfer: <span className="font-mono text-foreground">{req.achTransferId}</span>
                      {req.achInitiatedAt && <> • Initiated: {formatDate(req.achInitiatedAt)}</>}
                    </p>
                  )}

                  {/* Admin Notes */}
                  {req.adminNotes && (
                    <p className="text-xs text-muted-foreground bg-muted/30 p-2 rounded-lg">
                      <strong>Notes:</strong> {req.adminNotes}
                    </p>
                  )}

                  {/* Reject input */}
                  {showRejectInput === req.id && (
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Reason for rejection (optional)"
                        value={rejectNotes[req.id] || ''}
                        onChange={(e) => setRejectNotes(prev => ({ ...prev, [req.id]: e.target.value }))}
                        className="w-full bg-background border border-input rounded-lg py-2 px-3 text-foreground text-sm focus:outline-none focus:border-primary"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleReject(req.id)}
                          disabled={isActioning}
                          className="flex-1 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-medium rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
                        >
                          {isActioning ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                          Confirm Reject
                        </button>
                        <button
                          onClick={() => setShowRejectInput(null)}
                          className="px-4 py-2 bg-muted/40 text-muted-foreground rounded-lg text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  {showRejectInput !== req.id && (
                    <div className="flex flex-wrap gap-2">
                      {req.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(req.id)}
                            disabled={isActioning}
                            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-lg text-sm transition-colors"
                          >
                            {isActioning ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                            Approve
                          </button>
                          <button
                            onClick={() => setShowRejectInput(req.id)}
                            className="flex items-center gap-1.5 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg text-sm transition-colors"
                          >
                            <XCircle className="w-3.5 h-3.5" />
                            Reject
                          </button>
                        </>
                      )}
                      {req.status === 'approved' && (
                        <button
                          onClick={() => handleInitiateAch(req.id)}
                          disabled={isActioning}
                          className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-medium rounded-lg text-sm transition-colors"
                        >
                          {isActioning ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                          Initiate ACH Transfer
                        </button>
                      )}
                      {req.status === 'ach_initiated' && (
                        <button
                          onClick={() => handleSettle(req.id)}
                          disabled={isActioning}
                          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-medium rounded-lg text-sm transition-colors"
                        >
                          {isActioning ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                          Mark Settled
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── stat card ───────────────────────── */

function StatCard({
  label, count, usd, icon: Icon, color, accent,
}: {
  label: string; count: number; usd: number;
  icon: React.ElementType; color: string; accent: string;
}) {
  return (
    <div className={`bg-card border ${accent} rounded-xl p-4`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xl font-bold text-foreground">{count}</p>
      <p className={`text-sm font-medium mt-0.5 ${color}`}>{formatUsd(usd)}</p>
    </div>
  );
}
